import { useCallback, useEffect, useState } from "react";
import {
  getCurrentUser,
  getCachedUserSync,
  isAdminUser,
} from "@/services/currentUserService";

export type AdminAuthStatus = "loading" | "admin" | "denied" | "unavailable";

export const useAdminAuth = () => {
  const [status, setStatus] = useState<AdminAuthStatus>(() => {
    const cached = getCachedUserSync();
    if (!cached) return "loading";
    return isAdminUser(cached) ? "admin" : "denied";
  });
  const [retrying, setRetrying] = useState(false);

  const verify = useCallback(async (isRetry = false) => {
    if (isRetry) setRetrying(true);
    else if (!getCachedUserSync()) setStatus("loading");

    try {
      const data = await getCurrentUser(isRetry);
      setStatus(isAdminUser(data) ? "admin" : "denied");
    } catch (err: any) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        setStatus("denied");
      } else {
        setStatus("unavailable");
      }
    } finally {
      if (isRetry) setRetrying(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  return { status, retrying, retry: () => verify(true) };
};

const isSuperUser = (user: any) => !!user?.is_superuser;

/**
 * Same shape as useAdminAuth, but "admin" requires is_superuser specifically —
 * for pages like Flyer Control where every backend action (launch, cancel,
 * validate, search) already enforces IsSuperUser/is_superuser strictly, so a
 * staff-only account would just see a page full of actions it can't use.
 */
export const useSuperUserAuth = () => {
  const [status, setStatus] = useState<AdminAuthStatus>(() => {
    const cached = getCachedUserSync();
    if (!cached) return "loading";
    return isSuperUser(cached) ? "admin" : "denied";
  });
  const [retrying, setRetrying] = useState(false);

  const verify = useCallback(async (isRetry = false) => {
    if (isRetry) setRetrying(true);
    else if (!getCachedUserSync()) setStatus("loading");

    try {
      const data = await getCurrentUser(isRetry);
      setStatus(isSuperUser(data) ? "admin" : "denied");
    } catch (err: any) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        setStatus("denied");
      } else {
        setStatus("unavailable");
      }
    } finally {
      if (isRetry) setRetrying(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  return { status, retrying, retry: () => verify(true) };
};

export interface FlyerPermissions {
  isSuperUser: boolean;
  canSendFlyer: boolean;
  canSendBulkFlyer: boolean;
  canUploadCsvFlyer: boolean;
  // Matches the backend's actual rule for email_source: "selected" — either
  // permission unlocks it, since can_upload_csv_flyer is intentionally
  // self-contained (upload + send) and the launch endpoint can't tell which
  // flow produced a given selection.
  canSendSelected: boolean;
}

const emptyFlyerPermissions: FlyerPermissions = {
  isSuperUser: false, canSendFlyer: false, canSendBulkFlyer: false,
  canUploadCsvFlyer: false, canSendSelected: false,
};

const toFlyerPermissions = (user: any): FlyerPermissions => {
  const isSuper = !!user?.is_superuser;
  const canSendFlyer = !!user?.can_send_flyer;
  const canSendBulkFlyer = !!user?.can_send_bulk_flyer;
  const canUploadCsvFlyer = !!user?.can_upload_csv_flyer;
  return {
    isSuperUser: isSuper, canSendFlyer, canSendBulkFlyer, canUploadCsvFlyer,
    canSendSelected: isSuper || canSendFlyer || canUploadCsvFlyer,
  };
};

const hasAnyFlyerPermission = (user: any) => {
  const p = toFlyerPermissions(user);
  return p.isSuperUser || p.canSendFlyer || p.canSendBulkFlyer || p.canUploadCsvFlyer;
};

/**
 * Page-level access for Flyer Control: superuser, or any of the three
 * granular flags. Also exposes the raw flags (and canSendSelected) so the
 * page can show/hide individual sections per-permission.
 */
export const useFlyerAccess = () => {
  const [status, setStatus] = useState<AdminAuthStatus>(() => {
    const cached = getCachedUserSync();
    if (!cached) return "loading";
    return hasAnyFlyerPermission(cached) ? "admin" : "denied";
  });
  const [permissions, setPermissions] = useState<FlyerPermissions>(() => {
    const cached = getCachedUserSync();
    return cached ? toFlyerPermissions(cached) : emptyFlyerPermissions;
  });
  const [retrying, setRetrying] = useState(false);

  const verify = useCallback(async (isRetry = false) => {
    if (isRetry) setRetrying(true);
    else if (!getCachedUserSync()) setStatus("loading");

    try {
      const data = await getCurrentUser(isRetry);
      setPermissions(toFlyerPermissions(data));
      setStatus(hasAnyFlyerPermission(data) ? "admin" : "denied");
    } catch (err: any) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        setStatus("denied");
      } else {
        setStatus("unavailable");
      }
    } finally {
      if (isRetry) setRetrying(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  return { status, retrying, retry: () => verify(true), permissions };
};
